import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { FaExclamationTriangle } from 'react-icons/fa';
import './ContractForm.css';
import { generateContract } from '../functions/docxFunction';
import config from '../config/config';
import { getEventCountForDate } from '../utils/eventLimits';
import { useAuth } from '../hooks/useAuth';

const ContractForm = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    clientName: '',
    clientCin: '',
    clientTel: '',
    location: '',
    price: '',
    prepayment: '',
    prepaymentDate: '',
    paymentLeft: '',
    eventDate: '',
    contractDate: new Date().toISOString().split('T')[0],
    startTime: '',
    endTime: '',
    numberMusicians: '',
    description: '',
    singers: []
  });

  const [errors, setErrors] = useState({});
  const [successMessage, setSuccessMessage] = useState('');
  const [singers, setSingers] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [showSingerModal, setShowSingerModal] = useState(false);
  const [eventCount, setEventCount] = useState(0);
  const [showEventLimitWarning, setShowEventLimitWarning] = useState(false);

  const apiUrl = `${config.API_URL}/api`;

  // Fetch singers
  useEffect(() => {
    fetchSingers();
  }, []);

  const fetchSingers = async () => {
    try {
      const token = localStorage.getItem('token');
      console.log('🎤 Fetching singers...', { apiUrl, token: token ? 'present' : 'missing' });

      const response = await axios.get(`${apiUrl}/singers`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      console.log('🎤 Singers response:', response.data);
      setSingers(response.data || []);
    } catch (error) {
      console.error('❌ Error fetching singers:', error);
      setSingers([]); // Ensure singers is always an array
    }
  };

  // Calculate payment left when prepayment changes
  useEffect(() => {
    if (formData.price && formData.prepayment) {
      const paymentLeft = parseFloat(formData.price) - parseFloat(formData.prepayment);
      setFormData(prev => ({ ...prev, paymentLeft: paymentLeft.toString() }));
    }
  }, [formData.price, formData.prepayment]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));

    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }

    // Check event count when event date changes (only for non-admin users)
    if (name === 'eventDate' && value && user && user.role !== 'admin') {
      checkEventCountForDate(value);
    }
  };

  // Function to check event count for a specific date
  const checkEventCountForDate = async (date) => {
    try {
      const token = localStorage.getItem('token');
      const count = await getEventCountForDate(date, token);
      setEventCount(count);
      setShowEventLimitWarning(count >= 3);
    } catch (error) {
      console.error('Error checking event count:', error);
    }
  };

  const handleTimeChange = (e) => {
    const { name, value } = e.target;

    // Only allow digits and colon
    const sanitizedValue = value.replace(/[^0-9:]/g, '');

    // Auto-format as user types
    let formattedValue = sanitizedValue;

    // If user types more than 2 digits without colon, auto-insert colon
    if (sanitizedValue.length === 3 && !sanitizedValue.includes(':')) {
      formattedValue = sanitizedValue.slice(0, 2) + ':' + sanitizedValue.slice(2);
    }

    // Limit to HH:MM format (5 characters max)
    if (formattedValue.length <= 5) {
      setFormData(prev => ({ ...prev, [name]: formattedValue }));

      // Clear error when user starts typing
      if (errors[name]) {
        setErrors(prev => ({ ...prev, [name]: '' }));
      }
    }
  };

  const validateForm = () => {
    const newErrors = {};

    // Required fields validation
    const requiredFields = [
      'clientName', 'clientCin', 'clientTel', 'location',
      'price', 'prepayment', 'prepaymentDate', 'paymentLeft',
      'eventDate', 'startTime', 'endTime'
    ];

    requiredFields.forEach(field => {
      if (!formData[field] || formData[field] === '') {
        newErrors[field] = 'Ce champ est requis';
      }
    });

    // CIN validation (8 digits)
    if (formData.clientCin && !/^\d{8}$/.test(formData.clientCin)) {
      newErrors.clientCin = 'Le CIN doit contenir exactement 8 chiffres';
    }

    // Phone validation
    if (formData.clientTel && !/^\d{8}$/.test(formData.clientTel)) {
      newErrors.clientTel = 'Le numéro de téléphone doit contenir 8 chiffres';
    }

    // Price validation
    if (formData.price && isNaN(parseFloat(formData.price))) {
      newErrors.price = 'Le prix doit être un nombre valide';
    }

    // Prepayment validation
    if (formData.prepayment && isNaN(parseFloat(formData.prepayment))) {
      newErrors.prepayment = 'L\'acompte doit être un nombre valide';
    }

    // Payment left validation
    if (formData.paymentLeft && isNaN(parseFloat(formData.paymentLeft))) {
      newErrors.paymentLeft = 'Le montant restant doit être un nombre valide';
    }

    // Time validation (24-hour format)
    const timeRegex = /^([01]?[0-9]|2[0-3]):[0-5][0-9]$/;

    if (formData.startTime && !timeRegex.test(formData.startTime)) {
      newErrors.startTime = 'Format d\'heure invalide. Utilisez HH:MM (ex: 20:00)';
    }

    if (formData.endTime && !timeRegex.test(formData.endTime)) {
      newErrors.endTime = 'Format d\'heure invalide. Utilisez HH:MM (ex: 23:00)';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    console.log('🚀 Form submission started');

    if (!validateForm()) {
      console.log('❌ Form validation failed');
      return;
    }

    setLoading(true);
    console.log('⏳ Loading state set to true');

    try {
      const token = localStorage.getItem('token');
      console.log('🔑 Token retrieved:', token ? 'Yes' : 'No');

      if (!token) {
        console.log('❌ No authentication token found, redirecting to login');
        navigate('/login');
        return;
      }

      // Ensure paymentLeft is calculated
      let finalFormData = { ...formData };
      if (finalFormData.price && finalFormData.prepayment) {
        const paymentLeft = parseFloat(finalFormData.price) - parseFloat(finalFormData.prepayment);
        finalFormData.paymentLeft = paymentLeft.toString();
      } else {
        finalFormData.paymentLeft = finalFormData.price || '0';
      }

      // Ensure prepayment has a default value
      if (!finalFormData.prepayment || finalFormData.prepayment === '') {
        finalFormData.prepayment = '0';
      }

      // Ensure prepaymentDate has a default value
      if (!finalFormData.prepaymentDate || finalFormData.prepaymentDate === '') {
        finalFormData.prepaymentDate = finalFormData.contractDate;
      }

      const contractData = {
        ...finalFormData,
        singers: finalFormData.singers.map(singer => {
          // If singer is an object, extract the ID; if it's already an ID, use it
          return typeof singer === 'object' ? singer.id : singer;
        })
      };

      console.log('🔍 Sending contract data:', contractData);
      console.log('🎤 Singers being sent:', contractData.singers);
      console.log('🎤 Singers type:', typeof contractData.singers);
      console.log('🌐 API URL:', `${apiUrl}/contracts`);
      console.log('📤 Making POST request...');

      const response = await axios.post(`${apiUrl}/contracts`, contractData, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        timeout: 30000 // 30 second timeout
      });

      console.log('✅ Response received:', response.status, response.data);
      console.log('🆔 Contract ID from backend:', response.data.contractId);

      if (response.status === 201) {
        // Show success message
        setErrors({}); // Clear any existing errors
        setSuccessMessage('Contrat créé avec succès !');
        setLoading(false);

        // Update formData with the actual contractId from backend
        const updatedFormData = {
          ...formData,
          contractId: response.data.contractId
        };

        console.log('🆔 Updated form data with contract ID:', updatedFormData);
        console.log('🎤 Singers in updatedFormData:', updatedFormData.singers);
        console.log('🎤 Singers type in updatedFormData:', typeof updatedFormData.singers);
        if (updatedFormData.singers && updatedFormData.singers.length > 0) {
          console.log('🎤 First singer in updatedFormData:', updatedFormData.singers[0]);
          console.log('🎤 First singer type:', typeof updatedFormData.singers[0]);
        }

        // Generate contract document in background (non-blocking)
        generateContract(updatedFormData)
          .then(() => {
            console.log('✅ Contract document generated successfully');
          })
          .catch((docError) => {
            console.error('⚠️ Warning: Contract document generation failed:', docError);
            // Don't block the form submission - this is just a bonus feature
          });

        // Always navigate back to dashboard immediately
        console.log('✅ Contract created successfully, navigating to dashboard');

        // Small delay to show success state
        setTimeout(() => {
          navigate('/');
        }, 1000);
      }
    } catch (error) {
      console.error('❌ Error creating contract:', error);
      if (error.response) {
        console.error('❌ Response status:', error.response.status);
        console.error('❌ Response data:', error.response.data);
        console.error('❌ Response headers:', error.response.headers);

        // Check if it's an event limit error
        if (error.response.data && error.response.data.code === 'EVENT_LIMIT_EXCEEDED') {
          setErrors({ submit: error.response.data.message });
        } else {
          setErrors({ submit: 'Erreur lors de la création du contrat' });
        }
      } else if (error.request) {
        console.error('❌ Request error:', error.request);
        setErrors({ submit: 'Erreur lors de la création du contrat' });
      } else {
        console.error('❌ Error message:', error.message);
        setErrors({ submit: 'Erreur lors de la création du contrat' });
      }
    } finally {
      setLoading(false);
      console.log('🏁 Form submission completed, loading set to false');
    }
  };

  const handleAddSinger = (singer) => {
    if (!formData.singers.find(s => s.id === singer.id)) {
      setFormData(prev => ({
        ...prev,
        singers: [...prev.singers, singer]
      }));
    }
    setShowSingerModal(false);
  };

  const handleRemoveSinger = (singerId) => {
    setFormData(prev => ({
      ...prev,
      singers: prev.singers.filter(s => s.id !== singerId)
    }));
  };

  const filteredSingers = singers.filter(singer =>
    singer.name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Debug logging
  console.log('🎤 Singers state:', {
    singersCount: singers.length,
    filteredCount: filteredSingers.length,
    searchTerm,
    singers: singers
  });


  return (
    <div className="contract-form-page">
      {/* Top Bar with Back Button */}
      <div className="form-page-top-bar" style={{ display: 'flex', marginBottom: '16px' }}>
        <button
          type="button"
          className="btn btn-secondary"
          onClick={() => navigate('/')}
          style={{ display: 'inline-flex', alignItems: 'center', gap: '8px' }}
        >
          ← Retour
        </button>
      </div>

      {/* Page Header */}
      <div className="page-header">
        <div className="header-content">
          <h1>Nouveau Contrat</h1>
          <p className="page-subtitle">
            Créez un nouveau contrat pour un événement
          </p>
        </div>
        <div className="header-actions">
          {/* Actions can go here if needed in the future */}
        </div>
      </div>

      <form onSubmit={handleSubmit} className="contract-form">
        {/* Client Information */}
        <div className="form-section">
          <h2 className="section-title">Informations Client</h2>
          <div className="form-grid">
            <div className="form-group">
              <label htmlFor="clientName" className="form-label">
                Nom complet du client *
              </label>
              <input
                type="text"
                id="clientName"
                name="clientName"
                value={formData.clientName}
                onChange={handleInputChange}
                className={`form-control ${errors.clientName ? 'error' : ''}`}
                placeholder="Entrez le nom complet"
              />
              {errors.clientName && <div className="form-error">{errors.clientName}</div>}
            </div>

            <div className="form-group">
              <label htmlFor="clientCin" className="form-label">
                CIN *
              </label>
              <input
                type="text"
                id="clientCin"
                name="clientCin"
                value={formData.clientCin}
                onChange={handleInputChange}
                className={`form-control ${errors.clientCin ? 'error' : ''}`}
                placeholder="12345678"
                maxLength="8"
              />
              {errors.clientCin && <div className="form-error">{errors.clientCin}</div>}
            </div>

            <div className="form-group">
              <label htmlFor="clientTel" className="form-label">
                Téléphone *
              </label>
              <input
                type="tel"
                id="clientTel"
                name="clientTel"
                value={formData.clientTel}
                onChange={handleInputChange}
                className={`form-control ${errors.clientTel ? 'error' : ''}`}
                placeholder="12345678"
                maxLength="8"
              />
              {errors.clientTel && <div className="form-error">{errors.clientTel}</div>}
            </div>
          </div>
        </div>

        {/* Event Information */}
        <div className="form-section">
          <h2 className="section-title">Détails de l'Événement</h2>
          <div className="form-grid">
            <div className="form-group">
              <label htmlFor="location" className="form-label">
                Lieu *
              </label>
              <input
                type="text"
                id="location"
                name="location"
                value={formData.location}
                onChange={handleInputChange}
                className={`form-control ${errors.location ? 'error' : ''}`}
                placeholder="Adresse de l'événement"
              />
              {errors.location && <div className="form-error">{errors.location}</div>}
            </div>

            <div className="form-group">
              <label htmlFor="eventDate" className="form-label">
                Date de l'événement *
              </label>
              <input
                type="date"
                id="eventDate"
                name="eventDate"
                value={formData.eventDate}
                onChange={handleInputChange}
                className={`form-control ${errors.eventDate ? 'error' : ''}`}
              />
              {errors.eventDate && <div className="form-error">{errors.eventDate}</div>}

              {/* Event count warning for non-admin users */}
              {user && user.role !== 'admin' && formData.eventDate && (
                <div className="event-count-info">
                  <p className="event-count-text">
                    Événements le {formData.eventDate}: {eventCount}/3 (tous les utilisateurs)
                  </p>
                  {showEventLimitWarning && (
                    <div className="event-limit-warning">
                      <FaExclamationTriangle className="warning-icon" />
                      <span>Cette journée est complète! Il y a déjà 3 événements ce jour. Seul l'administrateur peut ajouter plus d'événements.</span>
                    </div>
                  )}
                </div>
              )}
            </div>

            <div className="form-group">
              <label htmlFor="startTime" className="form-label">
                Heure de début * (format 24h: 00:00 - 23:59)
              </label>
              <input
                type="text"
                id="startTime"
                name="startTime"
                value={formData.startTime}
                onChange={handleTimeChange}
                className={`form-control ${errors.startTime ? 'error' : ''}`}
                placeholder="20:00"
                maxLength="5"
              />
              {errors.startTime && <div className="form-error">{errors.startTime}</div>}
              <div className="form-help">Format: HH:MM (ex: 20:00, 21:30)</div>
            </div>

            <div className="form-group">
              <label htmlFor="endTime" className="form-label">
                Heure de fin * (format 24h: 00:00 - 23:59)
              </label>
              <input
                type="text"
                id="endTime"
                name="endTime"
                value={formData.endTime}
                onChange={handleTimeChange}
                className={`form-control ${errors.endTime ? 'error' : ''}`}
                placeholder="23:00"
                maxLength="5"
              />
              {errors.endTime && <div className="form-error">{errors.endTime}</div>}
              <div className="form-help">Format: HH:MM (ex: 23:00, 00:30)</div>
            </div>

            <div className="form-group">
              <label htmlFor="numberMusicians" className="form-label">
                Nombre de musiciens
              </label>
              <input
                type="number"
                id="numberMusicians"
                name="numberMusicians"
                value={formData.numberMusicians}
                onChange={handleInputChange}
                className="form-control"
                placeholder="0"
                min="0"
              />
            </div>

            <div className="form-group form-group-full">
              <label htmlFor="description" className="form-label">
                Description
              </label>
              <textarea
                id="description"
                name="description"
                value={formData.description}
                onChange={handleInputChange}
                className="form-control"
                rows="3"
                placeholder="Détails supplémentaires..."
              />
            </div>
          </div>
        </div>

        {/* Financial Information */}
        <div className="form-section">
          <h2 className="section-title">Informations Financières</h2>
          <div className="form-grid">
            <div className="form-group">
              <label htmlFor="price" className="form-label">
                Prix total *
              </label>
              <input
                type="number"
                id="price"
                name="price"
                value={formData.price}
                onChange={handleInputChange}
                className={`form-control ${errors.price ? 'error' : ''}`}
                placeholder="0.00"
                step="0.01"
                min="0"
              />
              {errors.price && <div className="form-error">{errors.price}</div>}
            </div>

            <div className="form-group">
              <label htmlFor="prepayment" className="form-label">
                Acompte
              </label>
              <input
                type="number"
                id="prepayment"
                name="prepayment"
                value={formData.prepayment}
                onChange={handleInputChange}
                className={`form-control ${errors.prepayment ? 'error' : ''}`}
                placeholder="0.00"
                step="0.01"
                min="0"
              />
              {errors.prepayment && <div className="form-error">{errors.prepayment}</div>}
            </div>

            <div className="form-group">
              <label htmlFor="prepaymentDate" className="form-label">
                Date d'acompte
              </label>
              <input
                type="date"
                id="prepaymentDate"
                name="prepaymentDate"
                value={formData.prepaymentDate}
                onChange={handleInputChange}
                className="form-control"
              />
            </div>

            <div className="form-group">
              <label htmlFor="paymentLeft" className="form-label">
                Reste à payer
              </label>
              <input
                type="number"
                id="paymentLeft"
                name="paymentLeft"
                value={formData.paymentLeft}
                onChange={handleInputChange}
                className="form-control"
                placeholder="0.00"
                step="0.01"
                min="0"
                readOnly
              />
            </div>

            <div className="form-group">
              <label htmlFor="contractDate" className="form-label">
                Date du contrat
              </label>
              <input
                type="date"
                id="contractDate"
                name="contractDate"
                value={formData.contractDate}
                onChange={handleInputChange}
                className="form-control"
              />
            </div>
          </div>
        </div>

        {/* Singers Section */}
        <div className="form-section">
          <div className="section-header">
            <h2 className="section-title">Chanteurs</h2>
            <button
              type="button"
              className="btn btn-secondary"
              onClick={() => setShowSingerModal(true)}
            >
              ➕ Ajouter Chanteur
            </button>
          </div>

          {formData.singers.length > 0 && (
            <div className="selected-singers">
              {formData.singers.map((singer) => (
                <div key={singer.id} className="singer-tag">
                  <span>{singer.name}</span>
                  <button
                    type="button"
                    onClick={() => handleRemoveSinger(singer.id)}
                    className="remove-singer"
                  >
                    ✕
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Form Actions */}
        <div className="form-actions">
          {successMessage && (
            <div className="alert alert-success">
              {successMessage}
            </div>
          )}
          {errors.submit && (
            <div className="alert alert-error">
              {errors.submit}
            </div>
          )}

          <div className="action-buttons">
            <button
              type="button"
              className="btn btn-secondary"
              onClick={() => navigate('/')}
            >
              Annuler
            </button>
            <button
              type="submit"
              className="btn btn-primary"
              disabled={loading}
            >
              {loading ? (
                <>
                  <span className="spinner"></span>
                  Création...
                </>
              ) : (
                'Créer le Contrat'
              )}
            </button>
          </div>
        </div>
      </form>

      {/* Singer Selection Modal */}
      {showSingerModal && (
        <div className="modal-overlay">
          <div className="modal">
            <div className="modal-header">
              <h3 className="modal-title">Sélectionner un Chanteur</h3>
              <button
                className="modal-close"
                onClick={() => setShowSingerModal(false)}
              >
                ✕
              </button>
            </div>
            <div className="modal-body">
              <div className="search-box">
                <input
                  type="text"
                  placeholder="Rechercher un chanteur..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="form-control"
                />
              </div>
              <div className="singers-list">
                {filteredSingers.length > 0 ? (
                  filteredSingers.map((singer) => (
                    <div
                      key={singer.id}
                      className="singer-item"
                      onClick={() => handleAddSinger(singer)}
                    >
                      <div className="singer-info">
                        <span className="singer-name">{singer.name}</span>
                        {singer.phoneNumbers && singer.phoneNumbers.length > 0 && (
                          <span className="singer-phone">{singer.phoneNumbers[0]}</span>
                        )}
                      </div>
                    </div>
                  ))
                ) : (
                  <div className="no-singers-message">
                    {singers.length === 0 ? (
                      <p>❌ Aucun chanteur trouvé. Vérifiez la connexion à la base de données.</p>
                    ) : (
                      <p>🔍 Aucun chanteur ne correspond à votre recherche.</p>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ContractForm;